import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Search, Plus, Target, TrendingUp, Calendar, Lock, Flame, Scan, Link as LinkIcon, X, Camera, UtensilsCrossed } from 'lucide-react';
import axios from 'axios';
import { API, getLocalDateString, getLocalISOString } from '../App';
import { toast } from 'sonner';
import UpgradeModal from '../components/UpgradeModal';
import IntermittentFasting from '../components/IntermittentFasting';
import BarcodeScanner from '../components/BarcodeScanner';
import RecipeScanner from '../components/RecipeScanner';
import HamburgerMenu from '../components/HamburgerMenu';
import WaterTracker from '../components/WaterTracker';
import MealTemplates from '../components/MealTemplates';
import MealsThatFit from '../components/MealsThatFit';
import NutritionHabits from '../components/NutritionHabits';
import MealPhotoScanner from '../components/MealPhotoScanner';

const Nutrition = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [dailyData, setDailyData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showGoalsSetting, setShowGoalsSetting] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showRecipeScanner, setShowRecipeScanner] = useState(false);
  const [showPhotoScanner, setShowPhotoScanner] = useState(false);
  const [processingBarcode, setProcessingBarcode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [viewMode, setViewMode] = useState('daily'); // 'daily', 'weekly', or 'fasting'
  const [isPremium, setIsPremium] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [showLeftovers, setShowLeftovers] = useState(false);
  const [previousDaysLogs, setPreviousDaysLogs] = useState([]);

  // Keep the selected calendar day while preserving the user's current local clock time.
  const getSelectedDateISOString = () => `${selectedDate}${getLocalISOString().slice(10)}`;

  // Manual entry form
  const [manualEntry, setManualEntry] = useState({
    food_name: '',
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    servings: '1'
  });

  // Goals form
  const [goalsForm, setGoalsForm] = useState({
    daily_calories: '',
    daily_protein: '',
    daily_carbs: '',
    daily_fats: ''
  });

  useEffect(() => {
    checkPremiumStatus();
  }, [selectedDate, user?.id]); // Re-check access and reload when date changes

  const checkPremiumStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/premium/status`);
      const premium = Boolean(response.data?.is_premium);
      setIsPremium(premium);

      if (premium) {
        await loadNutritionData();
      } else {
        setDailyData(null);
        setWeeklyData(null);
        setGoals(null);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking premium status:', error);
      setIsPremium(false);
      setDailyData(null);
      setWeeklyData(null);
      setGoals(null);
      setLoading(false);
      toast.error('Unable to verify nutrition access right now.');
    }
  };

  const loadNutritionData = async () => {
    setLoading(true);
    try {
      // Use selected date instead of always using today
      const dateToLoad = selectedDate;

      // Load daily data
      try {
        const dailyResponse = await axios.get(`${API}/nutrition/daily/${dateToLoad}`);
        setDailyData(dailyResponse.data);
      } catch (error) {
        if (error.response?.status === 403) {
          // Premium feature - show upgrade message
          setDailyData(null);
        } else {
          console.error('Error loading daily data:', error);
        }
      }

      // Load weekly data
      try {
        const weeklyResponse = await axios.get(`${API}/nutrition/weekly`);
        setWeeklyData(weeklyResponse.data);
      } catch (error) {
        if (error.response?.status === 403) {
          setWeeklyData(null);
        }
      }

      // Load goals
      try {
        const goalsResponse = await axios.get(`${API}/nutrition/goals`);
        setGoals(goalsResponse.data);
        setGoalsForm({
          daily_calories: goalsResponse.data.daily_calories,
          daily_protein: goalsResponse.data.daily_protein,
          daily_carbs: goalsResponse.data.daily_carbs,
          daily_fats: goalsResponse.data.daily_fats
        });
      } catch (error) {
        console.error('Error loading goals:', error);
      }
    } catch (error) {
      console.error('Error loading nutrition data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntrySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/nutrition/log`, {
        date: getSelectedDateISOString(),
        food_name: manualEntry.food_name,
        calories: parseFloat(manualEntry.calories),
        protein: parseFloat(manualEntry.protein),
        carbs: parseFloat(manualEntry.carbs),
        fats: parseFloat(manualEntry.fats),
        servings: parseFloat(manualEntry.servings)
      });

      toast.success('Food logged successfully!');
      setShowManualEntry(false);
      setManualEntry({
        food_name: '',
        calories: '',
        protein: '',
        carbs: '',
        fats: '',
        servings: '1'
      });
      loadNutritionData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log food');
    }
  };

  const handleGoalsSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/nutrition/goals`, {
        user_id: user.id,
        daily_calories: parseFloat(goalsForm.daily_calories),
        daily_protein: parseFloat(goalsForm.daily_protein),
        daily_carbs: parseFloat(goalsForm.daily_carbs),
        daily_fats: parseFloat(goalsForm.daily_fats)
      });

      toast.success('Goals updated successfully!');
      setShowGoalsSetting(false);
      loadNutritionData();
    } catch (error) {
      toast.error('Failed to update goals');
    }
  };

  const handleSearchFoods = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.post(`${API}/nutrition/search`, {
        query: searchQuery,
        pageSize: 10,
        pageNumber: 1
      });
      setSearchResults(response.data.foods);
      if (response.data.foods.length === 0) {
        toast.info('No results found. Try different keywords or use barcode scanner.');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Food search is a premium feature. Upgrade to access!');
      } else if (error.response?.status === 429) {
        toast.error(error.response.data.detail || 'USDA API rate limit reached. Please use barcode scanner or manual entry.');
      } else {
        const errorMsg = error.response?.data?.detail || 'Failed to search foods';
        toast.error(errorMsg);
      }
    } finally {
      setSearching(false);
    }
  };

  const handleAddFoodFromSearch = async (food) => {
    try {
      // Search results are for discovery only. Resolve the selected FDC ID
      // through Food Details before logging nutrition.
      const detailedResponse = await axios.get(`${API}/nutrition/search/${food.fdcId}`);
      const detailedFood = detailedResponse.data;

      const normalized = detailedFood.nutritionPerServing || detailedFood.nutritionPer100g;
      if (!normalized) {
        throw new Error('USDA did not return usable nutrition data for this food.');
      }

      const servingLabel = detailedFood.servingDescription || '100 g';

      await axios.post(`${API}/nutrition/log`, {
        date: getSelectedDateISOString(),
        food_name: `${detailedFood.description || food.description} â€” ${servingLabel}`,
        calories: normalized.calories || 0,
        protein: normalized.protein || 0,
        carbs: normalized.carbs || 0,
        fats: normalized.fats || 0,
        servings: 1
      });

      toast.success(`Food added (${servingLabel})`);      setShowSearch(false);
      setSearchQuery('');
      setSearchResults([]);
      loadNutritionData();
    } catch (error) {
      toast.error('Failed to add food');
    }
  };

  const handleBarcodeScanned = async (barcode) => {
    // Prevent duplicate processing
    if (processingBarcode) {
      console.log('Already processing a barcode, ignoring duplicate');
      return;
    }
    
    setProcessingBarcode(true);
    console.log('Barcode scanned:', barcode);
    setShowBarcodeScanner(false);
    
    toast.loading('Looking up product...');
    
    try {
      const response = await axios.get(`${API}/nutrition/barcode/${barcode}`);
      const product = response.data;
      
      toast.dismiss();
      toast.success(`Found: ${product.product_name}`);
      
      // Auto-fill manual entry form with barcode data (per serving)
      setManualEntry({
        food_name: `${product.product_name} ${product.brands ? '(' + product.brands + ')' : ''}`.trim(),
        calories: Math.round(product.nutrition_per_serving.energy_kcal) || '',
        protein: Math.round(product.nutrition_per_serving.proteins) || '',
        carbs: Math.round(product.nutrition_per_serving.carbohydrates) || '',
        fats: Math.round(product.nutrition_per_serving.fat) || '',
        servings: '1'
      });
      
      setShowManualEntry(true);
      
    } catch (error) {
      toast.dismiss();
      if (error.response?.status === 403) {
        toast.error('Barcode scanning is a premium feature. Upgrade to access!');
        setShowUpgradeModal(true);
      } else if (error.response?.status === 404) {
        toast.error('Product not found. Try searching by name or enter manually.');
      } else {
        toast.error('Failed to lookup barcode. Please try again.');
      }
    } finally {
      setProcessingBarcode(false);
    }
  };

  const handleOpenBarcodeScanner = () => {
    if (!isPremium) {
      toast.error('Barcode scanning is a premium feature!');
      setShowUpgradeModal(true);
      return;
    }
    setProcessingBarcode(false); // Reset flag when opening scanner
    setShowBarcodeScanner(true);
  };

  const handleOpenRecipeScanner = () => {
    if (!isPremium) {
      toast.error('Recipe scanner is a premium feature!');
      setShowUpgradeModal(true);
      return;
    }
    setShowRecipeScanner(true);
  };

  const handleSaveRecipeToLog = async (recipeData) => {
    try {
      await axios.post(`${API}/nutrition/log`, {
        date: getSelectedDateISOString(),
        food_name: recipeData.food_name,
        calories: recipeData.calories,
        protein: recipeData.protein,
        carbs: recipeData.carbs,
        fats: recipeData.fats,
        servings: recipeData.servings
      });

      toast.success('Meal logged successfully!');
      setShowRecipeScanner(false);
      loadNutritionData();
    } catch (error) {
      toast.error('Failed to log meal');
    }
  };

  const handleOpenLeftovers = async () => {
    if (!isPremium) {
      toast.error('Leftovers feature is premium only!');
      setShowUpgradeModal(true);
      return;
    }
    
    try {
      // Load logs from previous 7 days
      const logs = [];
      const today = new Date();
      
      for (let i = 1; i <= 7; i++) {
        const pastDate = new Date(today);
        pastDate.setDate(today.getDate() - i);
        const dateStr = getLocalDateString(pastDate);
        
        const response = await axios.get(`${API}/nutrition/logs/by-date/${dateStr}`);
        if (response.data.logs && response.data.logs.length > 0) {
          logs.push({
            date: dateStr,
            logs: response.data.logs
          });
        }
      }
      
      setPreviousDaysLogs(logs);
      setShowLeftovers(true);
    } catch (error) {
      toast.error('Failed to load previous meals');
      console.error(error);
    }
  };

  const handleCopyMeal = async (logId, foodName) => {
    try {
      await axios.post(`${API}/nutrition/copy-meal`, {
        log_id: logId,
        target_date: selectedDate
      });
      
      toast.success(`${foodName} added to today!`);
      setShowLeftovers(false);
      loadNutritionData();
    } catch (error) {
      toast.error('Failed to copy meal');
      console.error(error);
    }
  };

  const renderProgressBar = (current, goal, label, color) => {
    const percentage = Math.min((current / goal) * 100, 100);
    const remaining = Math.max(goal - current, 0);

    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-blood-400 font-bold uppercase tracking-wider">{label}</span>
          <span className="text-stone-400 text-sm">
            {current.toFixed(0)} / {goal} {label === 'Calories' ? 'kcal' : 'g'}
          </span>
        </div>
        <div className="w-full bg-stone-800 rounded-full h-4 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${color}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <p className="text-stone-500 text-xs">
          {remaining.toFixed(0)} {label === 'Calories' ? 'kcal' : 'g'} remaining
        </p>
      </div>
    );
  };

  const PremiumNutritionPreview = () => (
    <div className="space-y-6" data-testid="nutrition-premium-preview">
      <Card className="glass p-6 md:p-8 border border-blood-900/60">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-blood-950 border border-blood-800 flex items-center justify-center shrink-0">
            <Lock className="w-6 h-6 text-blood-400" />
          </div>
          <div>
            <p className="text-blood-500 text-xs font-bold uppercase tracking-[0.2em] mb-2">Premium Nutrition</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Track the fuel behind your training.</h2>
            <p className="text-stone-400 max-w-2xl">
              Premium unlocks food logging, calorie and macro targets, hydration, fasting, food search, scanners,
              meal templates, leftovers, and weekly nutrition trends.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          {[
            ['Daily Tracking', 'Calories, protein, carbs, fats, water and custom goals.'],
            ['Smart Logging', 'USDA search, barcode lookup, meal photos and recipe analysis.'],
            ['Planning Tools', 'Meal templates, leftovers and prepared-meal recommendations.'],
            ['Nutrition Insights', 'Weekly summaries, habits, achievements and fasting history.'],
          ].map(([title, description]) => (
            <div key={title} className="rounded-lg border border-stone-800 bg-stone-950/50 p-4">
              <p className="text-white font-bold">{title}</p>
              <p className="text-stone-500 text-sm mt-1">{description}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <Button
            onClick={() => setShowUpgradeModal(true)}
            className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold px-8 py-3 rounded-xl tracking-wider"
            data-testid="nutrition-preview-upgrade-btn"
          >
            UNLOCK NUTRITION
          </Button>
          <p className="text-stone-500 text-sm">
            Your personalized workouts and exercise substitutions stay free.
          </p>
        </div>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center">
        <div className="text-blood-400 text-xl font-bold tracking-wider">LOADING NUTRITION DATA...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black overflow-x-hidden">
      <HamburgerMenu />
      
      <div className="max-w-7xl mx-auto px-4 py-4 animate-fade-in">
        {/* Back Button */}
        <button onClick={() => navigate('/hub')} className="flex items-center gap-2 text-stone-400 hover:text-white mb-4 transition-colors" data-testid="back-to-hub-btn">
          <ArrowLeft className="w-4 h-4" />
          Back to Hub
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-blood-400 mb-1 tracking-wider">
            NUTRITION TRACKER
          </h1>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isPremium && (
                <span className="text-xs bg-gradient-to-r from-blood-700 to-blood-900 text-white px-2 py-0.5 rounded-full font-bold tracking-wider">
                  PREMIUM
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => isPremium ? navigate('/prepared-meals') : setShowUpgradeModal(true)}
                className="glass border-stone-700 text-stone-400 hover:bg-stone-800 text-xs"
                data-testid="prepared-meals-nav-btn"
              >
                <UtensilsCrossed className="w-3 h-3 mr-1" />Prepared Meals
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!isPremium) {
                    setShowUpgradeModal(true);
                    return;
                  }
                  if (viewMode === 'daily') setViewMode('weekly');
                  else if (viewMode === 'weekly') setViewMode('fasting');
                  else setViewMode('daily');
                }}
                className="glass border-stone-700 text-stone-400 hover:bg-stone-800 text-xs"
              >
                {viewMode === 'daily' && <><TrendingUp className="w-3 h-3 mr-1" />Weekly</>}
                {viewMode === 'weekly' && <><Flame className="w-3 h-3 mr-1" />Fasting</>}
                {viewMode === 'fasting' && <><Calendar className="w-3 h-3 mr-1" />Daily</>}
              </Button>
            </div>
          </div>
        </div>

        {!isPremium && <PremiumNutritionPreview />}

        {/* Daily View */}
        {viewMode === 'daily' && isPremium && (
          <div className="space-y-6">
            {/* Date Selector */}
            <Card className="glass p-4 border border-stone-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const currentDate = new Date(selectedDate);
                      currentDate.setDate(currentDate.getDate() - 1);
                      setSelectedDate(getLocalDateString(currentDate));
                    }}
                    className="text-blood-400 hover:bg-blood-950/50 font-bold text-xl"
                  >
                    ←
                  </Button>
                  
                  <div className="text-center">
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      max={getLocalDateString()}
                      className="bg-stone-900 border border-stone-700 text-white px-4 py-2 rounded-lg text-center"
                    />
                    {selectedDate === getLocalDateString() && (
                      <p className="text-blood-500 text-xs mt-1 font-bold uppercase tracking-wider">Today</p>
                    )}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const currentDate = new Date(selectedDate);
                      const tomorrow = new Date(currentDate);
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      const today = getLocalDateString();
                      if (getLocalDateString(tomorrow) <= today) {
                        setSelectedDate(getLocalDateString(tomorrow));
                      }
                    }}
                    disabled={selectedDate === getLocalDateString()}
                    className="text-blood-400 hover:bg-blood-950/50 font-bold text-xl"
                  >
                    →
                  </Button>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDate(getLocalDateString())}
                  className="glass border-stone-700 text-stone-400 hover:bg-stone-800 font-semibold"
                >
                  TODAY
                </Button>
              </div>
            </Card>

            {/* Water Tracker */}
            <WaterTracker selectedDate={selectedDate} />

            {/* Weekly Nutrition Habits */}
            {isPremium && <NutritionHabits />}

            {/* Meal Templates */}
            <MealTemplates selectedDate={selectedDate} onMealLogged={loadNutritionData} />

            {/* Prepared meals that fit remaining macros */}
            <MealsThatFit selectedDate={selectedDate} isPremium={isPremium} refreshKey={dailyData?.totals?.calories} />

            {/* Quick Actions */}
            <Card className="glass p-6 relative border border-stone-800">
              <h2 className="text-2xl font-bold text-blood-400 mb-4 tracking-wider">QUICK ACTIONS</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                <Button
                  onClick={() => setShowManualEntry(true)}
                  className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 rounded-xl tracking-wider"
                  disabled={!isPremium}
                  data-testid="manual-entry-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  MANUAL
                </Button>
                <Button
                  onClick={() => setShowPhotoScanner(true)}
                  className="bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-600 hover:to-amber-800 text-white font-bold py-6 rounded-xl tracking-wider"
                  disabled={!isPremium}
                  data-testid="photo-scanner-btn"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  PHOTO
                </Button>
                <Button
                  onClick={handleOpenBarcodeScanner}
                  className="bg-gradient-to-r from-blood-800 to-stone-900 hover:from-blood-700 hover:to-stone-800 text-white font-bold py-6 rounded-xl tracking-wider"
                  disabled={!isPremium}
                  data-testid="barcode-scanner-btn"
                >
                  <Scan className="w-5 h-5 mr-2" />
                  SCAN
                </Button>
                <Button
                  onClick={handleOpenRecipeScanner}
                  className="bg-gradient-to-r from-stone-800 to-stone-900 hover:from-stone-700 hover:to-stone-800 text-white font-bold py-6 rounded-xl tracking-wider"
                  disabled={!isPremium}
                  data-testid="recipe-scanner-btn"
                >
                  <LinkIcon className="w-5 h-5 mr-2" />
                  RECIPE
                </Button>
                <Button
                  onClick={() => setShowSearch(true)}
                  className="bg-gradient-to-r from-stone-700 to-stone-900 hover:from-stone-600 hover:to-stone-800 text-white font-bold py-6 rounded-xl tracking-wider"
                  disabled={!isPremium}
                  data-testid="search-btn"
                >
                  <Search className="w-5 h-5 mr-2" />
                  SEARCH
                </Button>
                <Button
                  onClick={handleOpenLeftovers}
                  className="bg-gradient-to-r from-emerald-700 to-emerald-900 hover:from-emerald-600 hover:to-emerald-800 text-white font-bold py-6 rounded-xl tracking-wider"
                  disabled={!isPremium}
                  data-testid="leftovers-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  LEFTOVERS
                </Button>
                <Button
                  onClick={() => setShowGoalsSetting(true)}
                  className="bg-gradient-to-r from-blood-700 to-stone-900 hover:from-blood-600 hover:to-stone-800 text-white font-bold py-6 rounded-xl tracking-wider"
                  disabled={!isPremium}
                  data-testid="goals-btn"
                >
                  <Target className="w-5 h-5 mr-2" />
                  GOALS
                </Button>
                <Button
                  onClick={() => setViewMode('fasting')}
                  className="bg-gradient-to-r from-blood-900 to-blood-700 hover:from-blood-800 hover:to-blood-600 text-white font-bold py-6 rounded-xl tracking-wider border-2 border-blood-500"
                  disabled={!isPremium}
                  data-testid="fasting-btn"
                >
                  <Flame className="w-5 h-5 mr-2" />
                  FASTING
                </Button>
              </div>
            </Card>

            {/* Daily Progress */}
            {isPremium && dailyData && (
              <Card className="glass p-8 border border-stone-800">
                <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
                  <Calendar className="w-6 h-6 mr-3 text-blood-500" />
                  TODAY'S PROGRESS
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderProgressBar(
                    dailyData.totals.calories,
                    dailyData.goals.daily_calories,
                    'Calories',
                    'bg-gradient-to-r from-blood-700 to-blood-900'
                  )}
                  {renderProgressBar(
                    dailyData.totals.protein,
                    dailyData.goals.daily_protein,
                    'Protein',
                    'bg-gradient-to-r from-blood-600 to-blood-800'
                  )}
                  {renderProgressBar(
                    dailyData.totals.carbs,
                    dailyData.goals.daily_carbs,
                    'Carbs',
                    'bg-gradient-to-r from-blood-800 to-stone-900'
                  )}
                  {renderProgressBar(
                    dailyData.totals.fats,
                    dailyData.goals.daily_fats,
                    'Fats',
                    'bg-gradient-to-r from-stone-700 to-stone-900'
                  )}
                </div>
              </Card>
            )}

            {/* Recent Entries */}
            {isPremium && dailyData && dailyData.logs && dailyData.logs.length > 0 && (
              <Card className="glass p-8 border border-stone-800">
                <h2 className="text-2xl font-bold text-blood-400 mb-6 tracking-wider">TODAY'S MEALS</h2>
                <div className="space-y-3">
                  {dailyData.logs.map((log, idx) => (
                    <div key={idx} className="glass p-4 rounded-lg border border-stone-800">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-blood-400 font-bold text-lg tracking-wide">{log.food_name}</p>
                          <p className="text-stone-400 text-sm">
                            {log.calories.toFixed(0)} cal • {log.protein.toFixed(0)}g protein • {log.carbs.toFixed(0)}g carbs • {log.fats.toFixed(0)}g fat
                          </p>
                        </div>
                        <span className="text-blood-500 text-sm font-semibold">{log.servings} serving(s)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Weekly View */}
        {viewMode === 'weekly' && isPremium && weeklyData && (
          <div className="space-y-6">
            <Card className="glass p-8 border border-stone-800">
              <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
                <TrendingUp className="w-6 h-6 mr-3 text-blood-500" />
                WEEKLY SUMMARY
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="glass p-4 rounded-lg border border-stone-800">
                  <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">Avg Daily Calories</p>
                  <p className="text-2xl font-bold text-blood-400">{weeklyData.weekly_totals.avg_daily_calories.toFixed(0)}</p>
                </div>
                <div className="glass p-4 rounded-lg border border-stone-800">
                  <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">Avg Daily Protein</p>
                  <p className="text-2xl font-bold text-blood-400">{weeklyData.weekly_totals.avg_daily_protein.toFixed(0)}g</p>
                </div>
                <div className="glass p-4 rounded-lg border border-stone-800">
                  <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">Avg Daily Carbs</p>
                  <p className="text-2xl font-bold text-blood-400">{weeklyData.weekly_totals.avg_daily_carbs.toFixed(0)}g</p>
                </div>
                <div className="glass p-4 rounded-lg border border-stone-800">
                  <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">Avg Daily Fats</p>
                  <p className="text-2xl font-bold text-blood-400">{weeklyData.weekly_totals.avg_daily_fats.toFixed(0)}g</p>
                </div>
              </div>

              <h3 className="text-xl font-bold text-white mb-4">Daily Breakdown</h3>
              <div className="space-y-3">
                {weeklyData.daily_summaries.map((day, idx) => (
                  <div key={idx} className="glass p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-white font-semibold">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
                      <span className="text-blue-300 text-sm">{day.entries_count} entries</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div>
                        <p className="text-blue-400">Calories</p>
                        <p className="text-white font-semibold">{day.total_calories.toFixed(0)}</p>
                      </div>
                      <div>
                        <p className="text-blue-400">Protein</p>
                        <p className="text-white font-semibold">{day.total_protein.toFixed(0)}g</p>
                      </div>
                      <div>
                        <p className="text-blue-400">Carbs</p>
                        <p className="text-white font-semibold">{day.total_carbs.toFixed(0)}g</p>
                      </div>
                      <div>
                        <p className="text-blue-400">Fats</p>
                        <p className="text-white font-semibold">{day.total_fats.toFixed(0)}g</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Fasting View */}
        {viewMode === 'fasting' && isPremium && (
          <div className="space-y-6">
            <IntermittentFasting user={user} />
          </div>
        )}

        {/* Manual Entry Modal */}
        {showManualEntry && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="glass p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold text-white mb-6">Manual Food Entry</h2>
              <form onSubmit={handleManualEntrySubmit} className="space-y-4">
                <div>
                  <label className="text-blue-300 text-sm mb-2 block">Food Name</label>
                  <input
                    type="text"
                    value={manualEntry.food_name}
                    onChange={(e) => setManualEntry({ ...manualEntry, food_name: e.target.value })}
                    className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-blue-300 text-sm mb-2 block">Calories</label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualEntry.calories}
                      onChange={(e) => setManualEntry({ ...manualEntry, calories: e.target.value })}
                      className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-blue-300 text-sm mb-2 block">Protein (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualEntry.protein}
                      onChange={(e) => setManualEntry({ ...manualEntry, protein: e.target.value })}
                      className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-blue-300 text-sm mb-2 block">Carbs (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualEntry.carbs}
                      onChange={(e) => setManualEntry({ ...manualEntry, carbs: e.target.value })}
                      className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-blue-300 text-sm mb-2 block">Fats (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualEntry.fats}
                      onChange={(e) => setManualEntry({ ...manualEntry, fats: e.target.value })}
                      className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-blue-300 text-sm mb-2 block">Servings</label>
                  <input
                    type="number"
                    step="0.1"
                    value={manualEntry.servings}
                    onChange={(e) => setManualEntry({ ...manualEntry, servings: e.target.value })}
                    className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold py-3 rounded-xl"
                  >
                    Add Food
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowManualEntry(false)}
                    variant="outline"
                    className="flex-1 glass border-red-400 text-red-300 hover:bg-red-500/20"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {/* Goals Setting Modal */}
        {showGoalsSetting && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="glass p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold text-white mb-6">Set Daily Goals</h2>
              <form onSubmit={handleGoalsSubmit} className="space-y-4">
                <div>
                  <label className="text-blue-300 text-sm mb-2 block">Daily Calories</label>
                  <input
                    type="number"
                    value={goalsForm.daily_calories}
                    onChange={(e) => setGoalsForm({ ...goalsForm, daily_calories: e.target.value })}
                    className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="text-blue-300 text-sm mb-2 block">Daily Protein (g)</label>
                  <input
                    type="number"
                    value={goalsForm.daily_protein}
                    onChange={(e) => setGoalsForm({ ...goalsForm, daily_protein: e.target.value })}
                    className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="text-blue-300 text-sm mb-2 block">Daily Carbs (g)</label>
                  <input
                    type="number"
                    value={goalsForm.daily_carbs}
                    onChange={(e) => setGoalsForm({ ...goalsForm, daily_carbs: e.target.value })}
                    className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="text-blue-300 text-sm mb-2 block">Daily Fats (g)</label>
                  <input
                    type="number"
                    value={goalsForm.daily_fats}
                    onChange={(e) => setGoalsForm({ ...goalsForm, daily_fats: e.target.value })}
                    className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="bg-blue-500/20 border border-blue-400 rounded-lg p-4">
                  <p className="text-blue-300 text-sm">
                    💡 <strong>Recommended:</strong> Moderate activity level: 2000 kcal, 150g protein, 250g carbs, 65g fat
                  </p>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-semibold py-3 rounded-xl"
                  >
                    Save Goals
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowGoalsSetting(false)}
                    variant="outline"
                    className="flex-1 glass border-red-400 text-red-300 hover:bg-red-500/20"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {/* Food Search Modal */}
        {showSearch && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className="glass p-8 max-w-2xl w-full my-8">
              <h2 className="text-2xl font-bold text-white mb-6">Search USDA Food Database</h2>
              <div className="flex gap-3 mb-6">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchFoods()}
                  placeholder="Search for foods..."
                  className="flex-1 glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Button
                  onClick={handleSearchFoods}
                  disabled={searching}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold px-6 rounded-xl"
                >
                  {searching ? 'Searching...' : 'Search'}
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {searchResults.map((food) => (
                    <div key={food.fdcId} className="glass p-4 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-white font-semibold">{food.description}</p>
                          {food.brandName && (
                            <p className="text-blue-300 text-sm">{food.brandName}</p>
                          )}
                          <p className="text-stone-400 text-xs mt-1">
                            Serving basis: {food.servingDescription || '100 g'}
                          </p>
                          {food.nutritionPerServing ? (
                            <div className="flex flex-wrap gap-4 mt-2 text-sm text-blue-300">
                              <span>{food.nutritionPerServing.calories.toFixed(0)} cal</span>
                              <span>{food.nutritionPerServing.protein.toFixed(1)}g protein</span>
                              <span>{food.nutritionPerServing.carbs.toFixed(1)}g carbs</span>
                              <span>{food.nutritionPerServing.fats.toFixed(1)}g fat</span>
                            </div>
                          ) : (
                            <div className="flex gap-4 mt-2 text-sm text-blue-300">
                              {(food.nutrients || []).slice(0, 4).map((nutrient, idx) => (
                                <span key={idx}>
                                  {nutrient.nutrientName}: {Number(nutrient.value || 0).toFixed(1)} {nutrient.unitName}
                                </span>
                              ))}
                            </div>
                          )}                        </div>
                        <Button
                          onClick={() => handleAddFoodFromSearch(food)}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg"
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end mt-6">
                <Button
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  variant="outline"
                  className="glass border-red-400 text-red-300 hover:bg-red-500/20"
                >
                  Close
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
      
      {/* Barcode Scanner */}
      {showBarcodeScanner && (
        <BarcodeScanner
          onScanSuccess={handleBarcodeScanned}
          onClose={() => setShowBarcodeScanner(false)}
        />
      )}
      
      {/* Recipe Scanner */}
      {showRecipeScanner && (
        <RecipeScanner
          onClose={() => setShowRecipeScanner(false)}
          onSaveToLog={handleSaveRecipeToLog}
        />
      )}
      
      {/* Meal Photo Scanner */}
      {showPhotoScanner && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="max-w-lg w-full">
            <MealPhotoScanner
              selectedDate={selectedDate}
              onMealLogged={() => {
                loadNutritionData();
                toast.success('Meal logged successfully!');
              }}
              onClose={() => setShowPhotoScanner(false)}
            />
          </div>
        </div>
      )}
      
      {/* Leftovers Modal */}
      {showLeftovers && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="glass p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-emerald-800">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-blood-400 tracking-wider">
                🍽️ LEFTOVERS - Previous 7 Days
              </h2>
              <Button
                variant="ghost"
                onClick={() => setShowLeftovers(false)}
                className="text-stone-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
            
            {previousDaysLogs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-stone-400 mb-4">No meals found in the past 7 days</p>
                <Button
                  onClick={() => setShowLeftovers(false)}
                  className="glass border-stone-700 text-stone-400 hover:bg-stone-800"
                >
                  Close
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {previousDaysLogs.map((dayData, dayIdx) => (
                  <div key={dayIdx} className="glass p-4 rounded-lg border border-stone-800">
                    <h3 className="text-lg font-bold text-blood-400 mb-3 tracking-wider">
                      {new Date(dayData.date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </h3>
                    
                    <div className="space-y-2">
                      {dayData.logs.map((log, logIdx) => (
                        <div key={logIdx} className="glass p-3 rounded-lg border border-stone-700 flex justify-between items-center">
                          <div className="flex-1">
                            <p className="text-white font-bold">{log.food_name}</p>
                            <p className="text-stone-400 text-sm">
                              {Math.round(log.calories)} cal • {Math.round(log.protein)}g protein • {Math.round(log.carbs)}g carbs • {Math.round(log.fats)}g fat
                            </p>
                          </div>
                          <Button
                            onClick={() => handleCopyMeal(log.id, log.food_name)}
                            className="bg-gradient-to-r from-emerald-600 to-emerald-800 hover:from-emerald-500 hover:to-emerald-700 text-white font-bold px-4 py-2 ml-4"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default Nutrition;
